import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatErr } from "../../../../lib/api";
import { Button } from "../../../../components/ui/button";
import { Eye, EyeOff, Pencil } from "lucide-react";
import { toast } from "sonner";
import { DetailField, SectionCard, StatusPill } from "./shared";
import { bankAccountTypeLabels, ledgerGroupLabels } from "./constants";
import { ACCOUNTING_BASE } from "./accountingNav";

function maskLocally(value) {
  const digits = String(value || "");
  if (!digits) return "-";
  if (digits.length <= 4) return "*".repeat(digits.length);
  return `${"*".repeat(digits.length - 4)}${digits.slice(-4)}`;
}

/**
 * Read-only detail view for a single Accounting Bank Account
 * (/client/society/accounting/bank-accounts/:id). The account number stays
 * masked until the user explicitly requests the audited reveal action - it
 * is never shown in full just because this page loaded.
 *
 * Phase 5 correction (spec section 9): clearly separates "Bank Information"
 * (the physical bank's own details) from "Accounting Information" (how it
 * maps into the double-entry ledger). The book balance shown here is always
 * the linked ledger's DERIVED balance - never a manually stored number.
 */
export default function BankAccountViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [balance, setBalance] = useState(null);
  const [revealedNumber, setRevealedNumber] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: accountRow } = await api.get(`/society/accounting/engine/bank-accounts/${id}`);
        setAccount(accountRow);
        const [balanceRes, ledgerRes] = await Promise.all([
          api.get(`/society/accounting/engine/bank-accounts/${id}/balance`).catch(() => ({ data: null })),
          accountRow.linkedLedgerId
            ? api.get(`/society/accounting/engine/ledgers/${accountRow.linkedLedgerId}`).catch(() => ({ data: null }))
            : Promise.resolve({ data: null }),
        ]);
        setBalance(balanceRes.data);
        setLedger(ledgerRes.data);
      } catch (err) {
        toast.error(formatErr(err, "Could not load this bank account"));
        navigate(`${ACCOUNTING_BASE}/bank-accounts`, { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const toggleReveal = async () => {
    if (revealedNumber) {
      setRevealedNumber(null);
      return;
    }
    try {
      const res = await api.post(`/society/accounting/engine/bank-accounts/${id}/reveal`);
      setRevealedNumber(res.data.accountNumber);
    } catch (err) {
      toast.error(formatErr(err, "Could not reveal account number"));
    }
  };

  if (loading) return <div className="text-sm text-[#6B7280]">Loading...</div>;
  if (!account) return null;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <Link to={`${ACCOUNTING_BASE}/bank-accounts`} className="text-xs font-semibold uppercase tracking-[0.16em] text-[#6B7280] hover:text-[#111827]">
            &larr; Bank Accounts
          </Link>
          <h2 className="mt-1 text-lg font-extrabold text-[#111827]">{account.bankName}</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => navigate(`${ACCOUNTING_BASE}/reports/bank-book?bankAccountId=${id}`)}>
            View Bank Book
          </Button>
          <Button onClick={() => navigate(`${ACCOUNTING_BASE}/bank-accounts/${id}/edit`)} className="bg-[#111827] text-white hover:bg-[#374151]">
            <Pencil className="mr-2 h-4 w-4" /> Edit
          </Button>
        </div>
      </div>

      <SectionCard title="Bank Information">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <DetailField label="Bank" value={account.bankName} />
          <DetailField label="Branch" value={account.branch} />
          <DetailField label="Account name" value={account.accountName} />
          <div>
            <div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Account number</div>
            <div className="mt-0.5 flex items-center gap-2 font-mono text-sm font-semibold text-[#111827]">
              {revealedNumber || maskLocally(account.accountNumber)}
              <button type="button" onClick={toggleReveal} className="text-[#6B7280] hover:text-[#111827]" title={revealedNumber ? "Hide" : "Reveal (audited)"}>
                {revealedNumber ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </button>
            </div>
          </div>
          <DetailField label="IFSC" value={account.ifsc} />
          <DetailField label="Account type" value={bankAccountTypeLabels[account.accountType] || account.accountType} />
        </div>
      </SectionCard>

      <SectionCard title="Accounting Information" subtitle="Balance is always derived from the linked ledger's opening balance plus posted vouchers - never entered or stored directly.">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <DetailField label="Linked ledger" value={account.linkedLedgerName} />
          <DetailField label="Ledger group" value={ledger ? (ledgerGroupLabels[ledger.ledgerGroup] || ledger.ledgerGroup) : "Bank"} />
          <DetailField label="Current book balance" value={balance ? `₹ ${Number(balance.balance || 0).toLocaleString()}` : "-"} />
          <div>
            <div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Reconciliation enabled</div>
            <div className="mt-1"><StatusPill active={account.reconciliationEnabled} trueLabel="Enabled" falseLabel="Disabled" /></div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Primary collection account</div>
            <div className="mt-1"><StatusPill active={account.isPrimaryCollectionAccount} trueLabel="Yes" falseLabel="No" /></div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Status</div>
            <div className="mt-1"><StatusPill active={account.active !== false} /></div>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-4 text-xs font-semibold text-[#111827]">
          {account.linkedLedgerId && (
            <Link to={`${ACCOUNTING_BASE}/ledgers/${account.linkedLedgerId}`} className="hover:underline">View Ledger</Link>
          )}
          <Link to={`${ACCOUNTING_BASE}/reports/bank-book?bankAccountId=${account.id}`} className="hover:underline">View Bank Book</Link>
          <Link to={`${ACCOUNTING_BASE}/bank-reconciliation?bankAccountId=${account.id}`} className="hover:underline">Reconcile</Link>
        </div>
      </SectionCard>

      <SectionCard title="Status History">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Ledger history</div>
            <div className="mt-1"><StatusPill active={!account.hasPostedTransactions} trueLabel="No posted vouchers yet" falseLabel="Has posted vouchers" /></div>
          </div>
          <DetailField label="Created" value={account.createdAt ? new Date(account.createdAt).toLocaleString() : "-"} />
          <DetailField label="Created by" value={account.createdBy} />
          <DetailField label="Last updated" value={account.updatedAt ? new Date(account.updatedAt).toLocaleString() : "-"} />
          <DetailField label="Updated by" value={account.updatedBy || account.createdBy} />
        </div>
      </SectionCard>
    </div>
  );
}
